import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Disc3, Building2, Shield } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

const FILTERS = ["all", "active", "suspended", "deactivated", "admin", "organizer", "dj"];

export const fmtDate = (iso, lang) => iso ? new Date(iso).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export const StatusPill = ({ u, t }) => {
  const st = u.deactivated ? "deactivated" : u.suspended ? "suspended" : "active";
  const cls = st === "active" ? "border-foreground/40 text-muted-foreground" : st === "suspended" ? "gg-status-postponed !border-transparent" : "gg-status-cancelled !border-transparent";
  return <span className={`eyebrow !text-[10px] px-1.5 py-0.5 border ${cls}`} data-testid={`user-status-${u.user_id}`}>{t(`userStatus.${st}`)}</span>;
};

export const RoleBadges = ({ u, t }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    <span className={`eyebrow !text-[10px] px-1.5 py-0.5 ${u.role === "admin" ? "bg-primary text-primary-foreground" : "border border-foreground/40"}`} data-testid={`user-role-${u.user_id}`}>{u.role === "admin" && <Shield className="w-3 h-3 inline mr-1 -mt-px" />}{t(`roles.${u.role}`)}</span>
    {u.is_organizer && u.role !== "organizer" && <span className="eyebrow !text-[10px] px-1.5 py-0.5 border border-foreground/40 inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{t("roles.organizer")}</span>}
    {u.is_dj && <span className="eyebrow !text-[10px] px-1.5 py-0.5 border border-foreground/40 inline-flex items-center gap-1"><Disc3 className="w-3 h-3" />DJ</span>}
  </span>
);

export default function AdminUsers() {
  const { user, t, lang, checkingAuth } = useApp();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const h = setTimeout(() => {
      setLoading(true);
      api.get("/admin/users", { params: { q: q || undefined, filter: filter === "all" ? undefined : filter } }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(h);
  }, [user?.role, q, filter]);
  if (checkingAuth) return null;
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="admin-users-page">
      <Link to="/admin" className="eyebrow inline-flex items-center gap-1.5 hover:text-primary" data-testid="back-to-admin"><ArrowLeft className="w-3.5 h-3.5" />{t("adminDashboard")}</Link>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-3 border-b border-foreground pb-3">
        <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tighter">{t("userManagement")}</h1>
        <span className="eyebrow text-muted-foreground" data-testid="users-count">{data.items.length} / {data.total} {t("usersLabel")}</span>
      </div>
      <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="gg-input !pl-9 !py-2" placeholder={t("searchUsers")} value={q} onChange={e => setQ(e.target.value)} data-testid="users-search" /></div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="users-filters">
          {FILTERS.map(f => <button key={f} onClick={() => setFilter(f)} className={`gg-chip !py-2 !px-3 whitespace-nowrap ${filter === f ? "gg-chip-active" : "hover:bg-muted"}`} data-testid={`users-filter-${f}`}>{t(`userFilters.${f}`)}</button>)}
        </div>
      </div>
      <div className="mt-4 border border-foreground overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]" data-testid="users-table">
          <thead><tr className="bg-muted/40 text-left">
            {["name", "email", "profileName", "location", "roleCol", "registered", "status", "eventsCol"].map(k => <th key={k} className="eyebrow !text-[10px] p-2.5 font-normal">{t(`userCols.${k}`)}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-foreground/20">
            {data.items.map(u => (
              <tr key={u.user_id} onClick={() => nav(`/admin/users/${u.user_id}`)} className="hover:bg-muted/50 transition-colors cursor-pointer" data-testid={`user-row-${u.user_id}`}>
                <td className="p-2.5"><Link to={`/admin/users/${u.user_id}`} className="font-display uppercase text-base hover:text-primary" data-testid={`user-link-${u.user_id}`}>{u.name || "—"}</Link></td>
                <td className="p-2.5 text-muted-foreground truncate max-w-[220px]">{u.email}</td>
                <td className="p-2.5">{u.dj_name || u.organizer_name || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-2.5 text-muted-foreground">{[u.location_city, u.location_country].filter(Boolean).join(", ") || "—"}</td>
                <td className="p-2.5"><RoleBadges u={u} t={t} /></td>
                <td className="p-2.5 text-muted-foreground whitespace-nowrap">{fmtDate(u.created_at, lang)}</td>
                <td className="p-2.5"><StatusPill u={u} t={t} /></td>
                <td className="p-2.5 tabular-nums" data-testid={`user-events-${u.user_id}`}>{u.events_count}</td>
              </tr>
            ))}
            {!loading && !data.items.length && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground" data-testid="users-empty">{t("noResults")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
